import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import React, { useState, useEffect, useMemo } from "react";
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  Clock,
  User,
  FileText,
  FlaskConical,
  Pencil,
  Plus,
  Eye,
  BarChart2,
  Activity,
  Microscope,
  ClipboardList,
  Calendar,
  Shield,
  ChevronDown,
  ChevronUp,
  Stethoscope,
  Pill,
  Trash2,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import {
  ROTULO_COLECAO,
  ColecaoClinica,
} from "../../../shared/utils/trilhaAuditoria";
import { useExames } from "../../exames/context/ExamesContext";
import { useEvolucao } from "../../evolucao/context/EvolucaoContext";
import { useMarcadores } from "../../marcadores/context/MarcadoresContext";
import { useSorologias } from "../../sorologias/context/SorologiasContext";
import { useExamesImagem } from "../../exames-imagem/context/ExamesImagemContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../lib/firebase";

interface AbaAuditoriaProps {
  paciente: Paciente;
}

export default function AbaAuditoria({ paciente }: AbaAuditoriaProps) {
  const { listarExamesPorPaciente } = useExames();
  const { listarEvolucoesPorPaciente } = useEvolucao();
  const { listarMarcadoresPorPaciente } = useMarcadores();
  const { listarSorologiasPorPaciente } = useSorologias();
  const { listarExamesPorPaciente: listarExamesImagemPorPaciente } =
    useExamesImagem();

  const exames = listarExamesPorPaciente(paciente.id);
  // B323 Bloco 3 (AI_146): consulta cancelada não conta na completude nem no
  // histórico de atividade (o cancelamento em si aparece na trilha, abaixo).
  const evolucoes = listarEvolucoesPorPaciente(paciente.id).filter(
    (e: any) => e.cancelado !== true
  );
  const marcadores = listarMarcadoresPorPaciente(paciente.id);
  const sorologias = listarSorologiasPorPaciente(paciente.id);
  const examesImagem = listarExamesImagemPorPaciente(paciente.id);

  const [secaoAberta, setSecaoAberta] = useState<string>("checklist");
  const [prescricoes, setPrescricoes] = useState<any[]>([]);

  // Carregar prescrições do Firebase
  useEffect(() => {
    const carregarPrescricoes = async () => {
      try {
        const q = query(
          collection(db, "prescricoes"),
          where("pacienteId", "==", paciente.id)
        );
        const snapshot = await getDocs(q);
        const lista = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          criadoEm:
            d.data().criadoEm?.toDate?.()?.toISOString?.() || d.data().criadoEm,
        }));
        setPrescricoes(lista);
      } catch (error) {
        console.error("Erro ao carregar prescrições:", error);
      }
    };
    carregarPrescricoes();
  }, [paciente.id]);

  // B213 — trilha de auditoria (edições/exclusões das coleções clínicas).
  // Ordenação no cliente p/ dispensar índice composto (lição B76).
  const [trilha, setTrilha] = useState<any[]>([]);
  useEffect(() => {
    const carregarTrilha = async () => {
      try {
        const q = query(
          collection(db, "trilha-auditoria"),
          where("pacienteId", "==", paciente.id)
        );
        const snapshot = await getDocs(q);
        setTrilha(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Erro ao carregar trilha de auditoria:", error);
      }
    };
    carregarTrilha();
  }, [paciente.id]);

  // ============================================
  // 1. CHECKLIST DE COMPLETUDE
  // ============================================
  const checklist = useMemo(() => {
    const items: {
      secao: string;
      item: string;
      preenchido: boolean;
      detalhe?: string;
    }[] = [];

    // DADOS PESSOAIS
    items.push({
      secao: "Dados Pessoais",
      item: "Nome completo",
      preenchido: !!paciente.nome?.trim(),
    });
    items.push({
      secao: "Dados Pessoais",
      item: "CPF",
      preenchido: !!paciente.cpf?.trim(),
    });
    items.push({
      secao: "Dados Pessoais",
      item: "Data de nascimento",
      preenchido: !!paciente.dataNascimento,
    });
    items.push({
      secao: "Dados Pessoais",
      item: "Sexo",
      preenchido: !!paciente.sexo,
    });
    items.push({
      secao: "Dados Pessoais",
      item: "Tipo sanguíneo",
      preenchido: !!(
        paciente.tipoSanguineo || (paciente as any).grupoSanguineo
      ),
    });
    items.push({
      secao: "Dados Pessoais",
      item: "Nome da mãe",
      preenchido: !!paciente.nomeMae?.trim(),
    });

    // CONTATO
    items.push({
      secao: "Contato",
      item: "Telefone",
      preenchido: !!telefoneVigente(paciente),
    });
    items.push({
      secao: "Contato",
      item: "Contato de emergência",
      preenchido: !!(
        paciente.contatoEmergenciaNome || paciente.contatoEmergencia?.nome
      ),
    });

    // ENDEREÇO
    items.push({
      secao: "Endereço",
      item: "Logradouro",
      preenchido: !!(paciente.logradouro || paciente.endereco?.rua),
    });
    items.push({
      secao: "Endereço",
      item: "Cidade/Estado",
      preenchido: !!(paciente.cidade && paciente.estado),
    });
    items.push({
      secao: "Endereço",
      item: "CEP",
      preenchido: !!(paciente.cep || paciente.endereco?.cep),
    });

    // DADOS CLÍNICOS
    items.push({
      secao: "Dados Clínicos",
      item: "Peso",
      preenchido: !!paciente.peso,
    });
    items.push({
      secao: "Dados Clínicos",
      item: "Altura",
      preenchido: !!paciente.altura,
    });
    items.push({
      secao: "Dados Clínicos",
      item: "Comorbidades",
      preenchido: !!paciente.comorbidades?.trim(),
    });
    items.push({
      secao: "Dados Clínicos",
      item: "Medicações em uso",
      preenchido: !!paciente.medicacoesEmUso?.trim(),
    });

    // EXAMES E ACOMPANHAMENTO
    items.push({
      secao: "Acompanhamento",
      item: "Exames laboratoriais",
      preenchido: exames.length > 0,
      detalhe:
        exames.length > 0 ? `${exames.length} coleta(s)` : "Nenhum exame",
    });
    items.push({
      secao: "Acompanhamento",
      item: "Consultas/evoluções",
      preenchido: evolucoes.length > 0,
      detalhe:
        evolucoes.length > 0
          ? `${evolucoes.length} consulta(s)`
          : "Nenhuma consulta",
    });
    items.push({
      secao: "Acompanhamento",
      item: "Marcadores/autoanticorpos",
      preenchido: marcadores.length > 0,
      detalhe:
        marcadores.length > 0
          ? `${marcadores.length} coleta(s)`
          : "Nenhuma coleta",
    });
    items.push({
      secao: "Acompanhamento",
      item: "Sorologias virais",
      preenchido: sorologias.length > 0,
      detalhe:
        sorologias.length > 0
          ? `${sorologias.length} coleta(s)`
          : "Nenhuma coleta",
    });
    items.push({
      secao: "Acompanhamento",
      item: "Exames de imagem",
      preenchido: examesImagem.length > 0,
      detalhe:
        examesImagem.length > 0
          ? `${examesImagem.length} exame(s)`
          : "Nenhum exame",
    });

    // TRANSPLANTE (se aplicável)
    if (paciente.tipoAtendimento === "figado") {
      items.push({
        secao: "Transplante",
        item: "Avaliação clínica / Inscrição Tx",
        preenchido: !!(
          paciente.inscricaoTx?.statusInscricao ||
          paciente.inscricaoTx?.numeroRGCT ||
          paciente.numeroRGCT ||
          paciente.rgct ||
          paciente.avaliacaoClinica?.causaCirrose
        ),
      });
      if (
        paciente.inscricaoTx?.dataTransplante ||
        paciente.dataTransplante ||
        paciente.transplantes?.length
      ) {
        items.push({
          secao: "Transplante",
          item: "Dados do transplante",
          preenchido: !!(
            paciente.dadosTransplante?.equipeCirurgica ||
            paciente.transplantes?.some((t) => t.equipeCirurgica)
          ),
        });
      }
    }

    return items;
  }, [paciente, exames, evolucoes, marcadores, sorologias, examesImagem]);

  const totalItems = checklist.length;
  const totalPreenchidos = checklist.filter((c) => c.preenchido).length;
  const percentual =
    totalItems > 0 ? Math.round((totalPreenchidos / totalItems) * 100) : 0;

  // Agrupar por seção
  const secoes = useMemo(() => {
    const map = new Map<string, typeof checklist>();
    checklist.forEach((item) => {
      const lista = map.get(item.secao) || [];
      lista.push(item);
      map.set(item.secao, lista);
    });
    return Array.from(map.entries());
  }, [checklist]);

  // ============================================
  // 2. RELATÓRIO DE ATIVIDADES
  // ============================================
  const atividades = useMemo(() => {
    const lista: {
      tipo: string;
      descricao: string;
      data: string;
      usuario?: string;
      icone: React.ElementType;
      cor: string;
    }[] = [];

    // Consultas/evoluções
    evolucoes.forEach((ev: any) => {
      lista.push({
        tipo: "Consulta",
        descricao: ev.tipo || "Consulta médica",
        data: ev.data || ev.criadoEm || "",
        usuario: ev.registradoPorNome || ev.medicoNome,
        icone: Stethoscope,
        cor: "#10b981",
      });
    });

    // Exames laboratoriais
    exames.forEach((ex: any) => {
      lista.push({
        tipo: "Exame Laboratorial",
        descricao: `Coleta de exames${ex.meld ? ` — MELD ${ex.meld}` : ""}`,
        data: ex.data || ex.criadoEm || "",
        usuario: ex.registradoPorNome,
        icone: FlaskConical,
        cor: "#6366f1",
      });
    });

    // Marcadores
    marcadores.forEach((m: any) => {
      lista.push({
        tipo: "Marcadores",
        descricao: "Coleta de marcadores/autoanticorpos",
        data: m.dataColeta || m.criadoEm || "",
        usuario: m.registradoPorNome,
        icone: Activity,
        cor: "#f59e0b",
      });
    });

    // Sorologias
    sorologias.forEach((s: any) => {
      lista.push({
        tipo: "Sorologias",
        descricao: "Coleta de sorologias virais",
        data: s.dataColeta || s.criadoEm || "",
        usuario: s.registradoPorNome,
        icone: Microscope,
        cor: "#ec4899",
      });
    });

    // Exames de imagem
    examesImagem.forEach((ei: any) => {
      lista.push({
        tipo: "Exame de Imagem",
        descricao: ei.tipo || "Exame de imagem",
        data: ei.data || ei.criadoEm || "",
        usuario: ei.registradoPorNome,
        icone: FileText,
        cor: "#0ea5e9",
      });
    });

    // Prescrições
    prescricoes.forEach((p: any) => {
      lista.push({
        tipo:
          p.tipo === "exames"
            ? "Pedido de Exames"
            : p.tipo === "receituario"
            ? "Receituário"
            : "Documento",
        descricao: p.titulo || "",
        data: p.criadoEm || "",
        usuario: p.criadoPorNome,
        icone: p.tipo === "receituario" ? Pill : ClipboardList,
        cor: p.tipo === "receituario" ? "#16a34a" : "#8b5cf6",
      });
    });

    // Ordenar por data (mais recente primeiro)
    lista.sort((a, b) => {
      const dA = new Date(a.data || 0).getTime();
      const dB = new Date(b.data || 0).getTime();
      return dB - dA;
    });

    return lista;
  }, [evolucoes, exames, marcadores, sorologias, examesImagem, prescricoes]);

  // ============================================
  // 3. LOG DE ALTERAÇÕES
  // ============================================
  // B110 (ficha do paciente) + B213 (trilha das coleções clínicas) num
  // feed único, mais recente primeiro.
  const logUnificado = useMemo(() => {
    const daFicha = (paciente.historicoEdicoes || []).map((h: any) => ({
      ...h,
      origem: "Ficha do paciente",
    }));
    const daTrilha = trilha.map((t: any) => ({
      data: t.data,
      usuarioNome: t.usuarioNome,
      acao: t.acao,
      camposAlterados: t.camposAlterados,
      origem: `${ROTULO_COLECAO[t.colecao as ColecaoClinica] || t.colecao}${
        t.rotulo ? ` · ${t.rotulo}` : ""
      }`,
    }));
    return [...daFicha, ...daTrilha].sort(
      (a, b) =>
        new Date(b.data || 0).getTime() - new Date(a.data || 0).getTime()
    );
  }, [paciente.historicoEdicoes, trilha]);

  // ============================================
  // HELPERS
  // ============================================
  const formatarData = (data: string) => {
    if (!data) return "—";
    try {
      return new Date(data).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return data;
    }
  };

  const formatarDataHora = (data: string) => {
    if (!data) return "—";
    try {
      return new Date(data).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return data;
    }
  };

  const getCorProgresso = (pct: number) => {
    if (pct >= 80) return "#16a34a";
    if (pct >= 50) return "#f59e0b";
    return "#dc2626";
  };

  const secaoConfig: { id: string; label: string; icon: React.ElementType }[] =
    [
      { id: "checklist", label: "Qualidade do Prontuário", icon: CheckCircle2 },
      {
        id: "atividades",
        label: "Relatório de Atividades",
        icon: ClipboardList,
      },
      { id: "log", label: "Log de Alterações", icon: Clock },
    ];

  // ============================================
  // RENDER
  // ============================================
  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            fontFamily: theme.fonts.sans,
            margin: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Shield size={18} style={{ marginRight: "8px" }} />
          Auditoria do Prontuário
        </h2>
      </div>

      {/* SUB-ABAS */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          marginBottom: "12px",
          borderBottom: `1px solid ${theme.gray[200]}`,
        }}
      >
        {secaoConfig.map((s) => (
          <button
            key={s.id}
            onClick={() => setSecaoAberta(s.id)}
            style={{
              padding: "8px 16px",
              background:
                secaoAberta === s.id ? theme.primary[600] : "transparent",
              color: secaoAberta === s.id ? "white" : theme.gray[600],
              border: "none",
              borderRadius: `${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0`,
              fontSize: "13px",
              fontWeight: "600",
              fontFamily: theme.fonts.sans,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
            onMouseOver={(e) => {
              if (secaoAberta !== s.id)
                e.currentTarget.style.background = theme.gray[100];
            }}
            onMouseOut={(e) => {
              if (secaoAberta !== s.id)
                e.currentTarget.style.background = "transparent";
            }}
          >
            <s.icon size={14} />
            {s.label}
          </button>
        ))}
      </div>

      {/* CONTEÚDO */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "calc(100vh - 340px)",
        }}
      >
        {/* ============================================ */}
        {/* CHECKLIST DE QUALIDADE */}
        {/* ============================================ */}
        {secaoAberta === "checklist" && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {/* BARRA DE PROGRESSO GERAL */}
            <div
              style={{
                background: theme.gray[50],
                borderRadius: theme.borderRadius.lg,
                padding: "16px",
                border: `1px solid ${theme.gray[200]}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: theme.gray[800],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Completude do Prontuário
                </span>
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: getCorProgresso(percentual),
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {percentual}%
                </span>
              </div>
              <div
                style={{
                  background: theme.gray[200],
                  borderRadius: "8px",
                  height: "10px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percentual}%`,
                    height: "100%",
                    background: getCorProgresso(percentual),
                    borderRadius: "8px",
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: "6px",
                  fontSize: "11px",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                {totalPreenchidos} de {totalItems} itens preenchidos
              </div>
            </div>

            {/* SEÇÕES DO CHECKLIST */}
            {secoes.map(([secaoNome, items]) => {
              const preenchidos = items.filter((i) => i.preenchido).length;
              const total = items.length;
              const pct = Math.round((preenchidos / total) * 100);

              return (
                <div
                  key={secaoNome}
                  style={{
                    border: `1px solid ${theme.gray[200]}`,
                    borderRadius: theme.borderRadius.md,
                    overflow: "hidden",
                  }}
                >
                  {/* Header da seção */}
                  <div
                    style={{
                      padding: "10px 12px",
                      background: pct === 100 ? "#f0fdf4" : theme.gray[50],
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: `1px solid ${theme.gray[200]}`,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: theme.gray[700],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {secaoNome}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: getCorProgresso(pct),
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {preenchidos}/{total}
                    </span>
                  </div>
                  {/* Items */}
                  <div style={{ padding: "8px 12px" }}>
                    {items.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "4px 0",
                          borderBottom:
                            i < items.length - 1
                              ? `1px solid ${theme.gray[100]}`
                              : "none",
                        }}
                      >
                        {item.preenchido ? (
                          <CheckCircle2 size={14} color="#16a34a" />
                        ) : (
                          <Circle size={14} color={theme.gray[300]} />
                        )}
                        <span
                          style={{
                            fontSize: "12px",
                            fontFamily: theme.fonts.sans,
                            color: item.preenchido
                              ? theme.gray[700]
                              : theme.gray[400],
                            flex: 1,
                          }}
                        >
                          {item.item}
                        </span>
                        {item.detalhe && (
                          <span
                            style={{
                              fontSize: "10px",
                              color: item.preenchido
                                ? "#16a34a"
                                : theme.gray[400],
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            {item.detalhe}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ============================================ */}
        {/* RELATÓRIO DE ATIVIDADES */}
        {/* ============================================ */}
        {secaoAberta === "atividades" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* RESUMO */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              {[
                {
                  label: "Consultas",
                  valor: evolucoes.length,
                  cor: "#10b981",
                  icon: Stethoscope,
                },
                {
                  label: "Lab.",
                  valor: exames.length,
                  cor: "#6366f1",
                  icon: FlaskConical,
                },
                {
                  label: "Marcadores",
                  valor: marcadores.length,
                  cor: "#f59e0b",
                  icon: Activity,
                },
                {
                  label: "Sorologias",
                  valor: sorologias.length,
                  cor: "#ec4899",
                  icon: Microscope,
                },
                {
                  label: "Imagem",
                  valor: examesImagem.length,
                  cor: "#0ea5e9",
                  icon: FileText,
                },
                {
                  label: "Prescrições",
                  valor: prescricoes.length,
                  cor: "#8b5cf6",
                  icon: ClipboardList,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    background: theme.gray[50],
                    border: `1px solid ${theme.gray[200]}`,
                    borderRadius: theme.borderRadius.md,
                    padding: "10px",
                    textAlign: "center",
                  }}
                >
                  <item.icon
                    size={16}
                    color={item.cor}
                    style={{ marginBottom: "4px" }}
                  />
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: item.cor,
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {item.valor}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* TIMELINE DE ATIVIDADES */}
            {atividades.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px",
                  color: theme.gray[400],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Nenhuma atividade registrada
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  paddingLeft: "24px",
                }}
              >
                {/* Linha vertical */}
                <div
                  style={{
                    position: "absolute",
                    left: "7px",
                    top: "8px",
                    bottom: "8px",
                    width: "2px",
                    background: theme.gray[200],
                  }}
                />
                {atividades.slice(0, 50).map((at, i) => (
                  <div
                    key={i}
                    style={{
                      position: "relative",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                    }}
                  >
                    {/* Bolinha */}
                    <div
                      style={{
                        position: "absolute",
                        left: "-21px",
                        top: "6px",
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: at.cor,
                        border: "2px solid white",
                        boxShadow: "0 0 0 1px " + theme.gray[200],
                      }}
                    />
                    {/* Conteúdo */}
                    <div
                      style={{
                        flex: 1,
                        padding: "6px 10px",
                        background: theme.gray[50],
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "12px",
                        fontFamily: theme.fonts.sans,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <at.icone size={12} color={at.cor} />
                        <span
                          style={{ fontWeight: "600", color: theme.gray[700] }}
                        >
                          {at.tipo}
                        </span>
                        <span style={{ color: theme.gray[500] }}>
                          {at.descricao}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "10px",
                          color: theme.gray[400],
                        }}
                      >
                        {at.usuario && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <User size={10} /> {at.usuario}
                          </span>
                        )}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <Calendar size={10} /> {formatarData(at.data)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {atividades.length > 50 && (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "11px",
                      color: theme.gray[400],
                      padding: "8px",
                    }}
                  >
                    Mostrando as 50 atividades mais recentes de{" "}
                    {atividades.length}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* LOG DE ALTERAÇÕES */}
        {/* ============================================ */}
        {secaoAberta === "log" && (
          <div>
            {logUnificado.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  background: theme.gray[50],
                  borderRadius: theme.borderRadius.lg,
                  color: theme.gray[400],
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Clock
                  size={32}
                  color={theme.gray[300]}
                  style={{ marginBottom: "8px" }}
                />
                <p>Nenhum registro de alteração</p>
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {logUnificado.map((historico: any, index: number) => {
                    const acaoConfig: Record<
                      string,
                      { icone: React.ElementType; cor: string; label: string }
                    > = {
                      cadastro: {
                        icone: Plus,
                        cor: theme.primary[600],
                        label: "Cadastro",
                      },
                      visualizacao: {
                        icone: Eye,
                        cor: theme.gray[500],
                        label: "Visualização",
                      },
                      edicao: {
                        icone: Pencil,
                        cor: "#f59e0b",
                        label: "Edição",
                      },
                      // B213 — lápide de exclusão (o registro foi removido,
                      // o rastro permanece)
                      exclusao: {
                        icone: Trash2,
                        cor: "#dc2626",
                        label: "Exclusão",
                      },
                      evolucao: {
                        icone: BarChart2,
                        cor: "#10b981",
                        label: "Nova Evolução",
                      },
                      exame: {
                        icone: FlaskConical,
                        cor: "#6366f1",
                        label: "Novo Exame",
                      },
                    };
                    const config = acaoConfig[historico.acao] || {
                      icone: FileText,
                      cor: theme.gray[600],
                      label: historico.acao,
                    };

                    return (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px 12px",
                          background: theme.gray[50],
                          borderRadius: theme.borderRadius.md,
                          borderLeft: `3px solid ${config.cor}`,
                        }}
                      >
                        <config.icone size={16} color={config.cor} />
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: "600",
                              fontFamily: theme.fonts.sans,
                              color: theme.gray[800],
                              fontSize: "13px",
                            }}
                          >
                            {config.label}
                            {/* B213 — de onde veio a alteração */}
                            {historico.origem && (
                              <span
                                style={{
                                  fontWeight: "400",
                                  color: theme.gray[500],
                                  fontSize: "12px",
                                }}
                              >
                                {" "}
                                — {historico.origem}
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: theme.gray[500],
                              display: "flex",
                              gap: "12px",
                              fontFamily: theme.fonts.sans,
                              marginTop: "2px",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <User size={10} /> {historico.usuarioNome}
                            </span>
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <Clock size={10} />{" "}
                              {formatarDataHora(historico.data)}
                            </span>
                          </div>
                          {/* B110 — campos alterados nesta edição */}
                          {Array.isArray(historico.camposAlterados) &&
                            historico.camposAlterados.length > 0 && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: theme.gray[600],
                                  fontFamily: theme.fonts.sans,
                                  marginTop: "3px",
                                }}
                              >
                                Campos: {historico.camposAlterados.join(", ")}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
